import { AppShell } from "@/components/layouts/AppShell";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { useLocation } from "wouter";
import { HeaderActionGroup, HeaderTabNav } from "@/components/layouts/header-slots";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { User, HelpCircle, MessageSquare, Book, AlertCircle, CheckCircle, ChevronDown, Download, CreditCard, Calendar, Building2, Shield } from "lucide-react";
import { LogoLoader } from "@/components/ui/logo-loader";
import { CornerLabelField } from "@/components/ui/corner-label-field";
import MembersTab from "./settings/MembersTab";
import OrganizationTab from "./settings/OrganizationTab";
import RolesTab from "./settings/RolesTab";
import ProjectsTab from "./settings/ProjectsTab";
import SubscriptionTab from "./settings/SubscriptionTab";
import SupportTab from "./settings/SupportTab";
import AccountTab from "./settings/AccountTab";
import SettingsInfoChips from "./settings/SettingsInfoChips";
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useAlert } from "@/hooks/use-alert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { SubscriptionUpgradeDialog } from "@/components/SubscriptionUpgradeDialog";
import { SupportDialog } from "@/components/SupportDialog";
import { useTranslation } from 'react-i18next';
// duplicate removed
import { usePermissions } from "@/hooks/use-permissions";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useUserPreferences, useUpdatePreferences } from "@/hooks/use-preferences";
import { useSubscriptionPlans, useOrganizationSubscription, useSubscriptionLimits } from "@/hooks/use-subscription";
import { apiClient } from "@/lib/api-client";
import { apiRequest } from "@/lib/queryClient";
import { subscriptionPlanNameToSlug } from '@/utils/subscription-plan-slug';
import { PermissionButton } from "@/components/PermissionButton";
import { PermissionInput } from "@/components/PermissionInput";
import { PermissionSelect } from "@/components/PermissionSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { RequirePermission } from "@/components/RequirePermission";
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { trackEvent } from "@/lib/analytics";
import { ANALYTICS_EVENTS } from "@mangelapp/shared";

// Password change schema - will be created dynamically with translations
type PasswordChangeFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

// Create password change schema function
const createPasswordChangeSchema = (t: (key: string) => string) => z.object({
  currentPassword: z.string().min(1, t('pages.settings:sections.password.validation.currentRequired')),
  newPassword: z.string().min(6, t('pages.settings:sections.password.validation.newMinLength')),
  confirmPassword: z.string().min(1, t('pages.settings:sections.password.validation.confirmRequired')),
});

export default function Settings() {
  const { t, i18n } = useTranslation(['pages.settings']);
  const { confirm } = useAlert();
  // duplicate removed
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const queryClient = useQueryClient();
  
  // Organization selector hook - must be called before usePermissions
  const { activeOrganizationId, activeOrgRole } = useOrganizationSelector();
  
  const { hasPermission, hasAnyPermission } = usePermissions({ organizationId: activeOrganizationId });
  const [activeTab, setActiveTab] = useState("account");
  const isRestrictedRole = activeOrgRole === 'watcher' || activeOrgRole === 'business_owner';
  const [activeProfileSection, setActiveProfileSection] = useState("profil");
  const [location, setLocation] = useLocation();

  // GA4: settings_opened when viewing settings
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SETTINGS_OPENED);
  }, []);

  // Keep active tab in sync with URL query (supports browser back/forward)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const allowedTabs = ['account', 'projects', 'organization', 'members', 'roles', 'subscription', 'support', 'faq'];

    if (tabParam && allowedTabs.includes(tabParam)) {
      setActiveTab(tabParam);
      return;
    }

    // Fall back to default tab when URL has no/invalid tab.
    setActiveTab('account');
  }, [location]);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    setLocation(`/settings?${params.toString()}`, { replace: true });
  };

  const prefetchOrgScopedData = async () => {
    const orgMeKey = ["/api/orgs/me", activeOrganizationId];
    await queryClient.prefetchQuery({
      queryKey: orgMeKey,
      queryFn: async () => {
        const url = activeOrganizationId
          ? `/api/orgs/me?organizationId=${encodeURIComponent(activeOrganizationId)}`
          : '/api/orgs/me';
        const res = await apiRequest(url);
        return await res.json();
      },
      staleTime: 60_000,
    });

    const cachedOrgMe = queryClient.getQueryData(orgMeKey) as any;
    const prefetchOrgId = cachedOrgMe?.data?.id as string | undefined;
    if (!prefetchOrgId) return;

    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: ["/api/orgs", prefetchOrgId, "members"],
        queryFn: async () => {
          const res = await apiRequest(`/api/orgs/${prefetchOrgId}/members`);
          return await res.json();
        },
      }),
      queryClient.prefetchQuery({
        queryKey: ["/api/orgs", prefetchOrgId, "invitations"],
        queryFn: async () => {
          const res = await apiRequest(`/api/orgs/${prefetchOrgId}/invitations`);
          return await res.json();
        },
      }),
      queryClient.prefetchQuery({
        queryKey: ["/api/orgs", prefetchOrgId, "roles"],
        queryFn: async () => {
          const res = await apiRequest(`/api/orgs/${prefetchOrgId}/roles`);
          return await res.json();
        },
      }),
    ]);
  };

  const prefetchSubscriptionData = async () => {
    if (!activeOrganizationId) return;

    const prefetches: Array<Promise<unknown>> = [
      queryClient.prefetchQuery({
        queryKey: ['/api/subscriptions/plans'],
        queryFn: async () => {
          const response = await apiRequest('/api/subscriptions/plans');
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to fetch subscription plans');
          }
          return data.plans;
        },
        staleTime: 10 * 60 * 1000,
      }),
      queryClient.prefetchQuery({
        queryKey: ['/api/subscriptions/organization', activeOrganizationId],
        queryFn: async () => {
          const response = await apiRequest(`/api/subscriptions/organization/${activeOrganizationId}`);
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to fetch subscription');
          }
          return data.subscription;
        },
        staleTime: 5 * 60 * 1000,
      }),
      queryClient.prefetchQuery({
        queryKey: ['/api/subscriptions/organization', activeOrganizationId, 'usage'],
        queryFn: async () => {
          const response = await apiRequest(`/api/subscriptions/organization/${activeOrganizationId}/usage`);
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to fetch subscription limits');
          }
          return data.usage;
        },
        staleTime: 2 * 60 * 1000,
      }),
    ];

    if (hasPermission('manage_organization')) {
      prefetches.push(
        queryClient.prefetchQuery({
          queryKey: ["/api/stripe/subscription"],
          queryFn: async () => apiClient.customRequest("/api/stripe/subscription"),
        }),
        queryClient.prefetchQuery({
          queryKey: ["/api/stripe/invoices"],
          queryFn: async () => apiClient.customRequest("/api/stripe/invoices"),
        })
      );
    }

    await Promise.all(prefetches);
  };

  const prefetchSettingsTab = (tabId: string) => {
    if (tabId === "subscription") {
      void prefetchSubscriptionData();
      return;
    }

    if (tabId === "members" || tabId === "organization" || tabId === "roles") {
      void prefetchOrgScopedData();
      if (tabId === "members" || tabId === "organization") {
        void prefetchSubscriptionData();
      }
    }
  };

  useEffect(() => {
    if (isRestrictedRole) return;
    if (typeof window === 'undefined') return;

    const scheduleIdle = (window as any).requestIdleCallback as
      | ((callback: () => void) => number)
      | undefined;
    const cancelIdle = (window as any).cancelIdleCallback as
      | ((id: number) => void)
      | undefined;

    let idleId: number | null = null;
    const timeoutId = window.setTimeout(() => {
      const runPrefetch = () => {
        prefetchSettingsTab('profile');
        prefetchSettingsTab('members');
      };

      if (scheduleIdle) {
        idleId = scheduleIdle(runPrefetch);
      } else {
        runPrefetch();
      }
    }, 800);

    return () => {
      window.clearTimeout(timeoutId);
      if (idleId !== null && cancelIdle) {
        cancelIdle(idleId);
      }
    };
  }, [isRestrictedRole, activeOrganizationId, hasPermission]);
  
  const { user: currentUser, isLoading: userLoading, error: userError } = useCurrentUser();
  const resolvedUserCode = currentUser?.userCode || null;
  const resolvedRoleKey = (currentUser as any)?.resolvedOrgRole ?? (currentUser as any)?.orgRole ?? null;
  const resolvedRoleLabel = resolvedRoleKey
    ? t(`pages.settings:sections.profile.roles.${resolvedRoleKey}`, {
        defaultValue: resolvedRoleKey,
      })
    : t('pages.settings:sections.profile.roles.unknown');

  // Preferences hooks
  const { data: preferences, isLoading: preferencesLoading } = useUserPreferences();
  const updatePreferences = useUpdatePreferences();

  const needsSubscriptionData = activeTab === 'subscription' || activeTab === 'members' || activeTab === 'organization';

  // Subscription data is only needed for subscription/members/organization tabs.
  const { data: subscriptionPlans, isLoading: plansLoading } = useSubscriptionPlans({
    enabled: activeTab === 'subscription',
  });
  const { data: organizationSubscription, isLoading: subscriptionLoading } = useOrganizationSubscription({
    enabled: needsSubscriptionData,
  });
  const { data: subscriptionLimits, isLoading: limitsLoading } = useSubscriptionLimits({
    enabled: needsSubscriptionData,
  });

  // Debug logging
  // console.log('🔍 Settings - User loading:', userLoading);
  // console.log('🔍 Settings - User error:', userError);
  // console.log('🔍 Settings - Current user (nested):', currentUser);
  // console.log('🔍 Settings - Current user orgRole:', currentUser?.orgRole);
  // console.log('🔍 Settings - Current user role:', currentUser?.role);
  const [currentUserCode, setCurrentUserCode] = useState("MP-010-250001");
  const [isProfileInitialized, setIsProfileInitialized] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordChangeResult, setPasswordChangeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [inviteRole, setInviteRole] = useState<string>('admin');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedProUsers, setSelectedProUsers] = useState(1);
  const [selectedRiseUsers, setSelectedRiseUsers] = useState(1);
  const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
  // Invite success modal state
  const [inviteSentOpen, setInviteSentOpen] = useState(false);
  const [inviteSentEmail, setInviteSentEmail] = useState<string | null>(null);
  // Remove old hardcoded subscription state - now using real subscription hooks
  const rqClient = useQueryClient();
  
  // Create password change schema with translations
  const passwordChangeSchema = useMemo(() => createPasswordChangeSchema(t), [t]);
  
  // Role management state
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([]);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  
  // Upgrade dialog state
  const [upgradeDialog, setUpgradeDialog] = useState({
    open: false,
    planId: '',
    planName: '',
    price: '',
    duration: '',
    billing: 'monthly' as 'monthly' | 'yearly',
    selectedUsers: 1,
    currentUsers: 1,
    maxUsers: 1,
    maxProjects: 1,
    features: [] as string[],
    isPerSeat: false,
    pricePerSeat: 0,
    minSeats: 1,
    maxSeats: null as number | null
  });

  // Erfolgs-Dialog State
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    planName: '',
    users: 1,
    billing: 'monthly',
    price: ''
  });
  
  // Support Dialog State
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  
  // Organization data
  const { data: orgMeResponse, isLoading: orgMeLoading } = useQuery({
    queryKey: ["/api/orgs/me", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId 
        ? `/api/orgs/me?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : '/api/orgs/me';
      const res = await apiRequest(url);
      return await res.json();
    },
    staleTime: 60_000,
    retry: (failureCount, error: any) => false,
    // Needed for Members, Organization, and Roles/Permissions tabs
    enabled: activeTab === "members" || activeTab === "organization" || activeTab === "roles",
  });
  const orgInfo = (orgMeResponse as any)?.data || null;
  const orgId = orgInfo?.id || null;
  const canManageRoles = hasPermission('manage_organization');

  const { data: membersResponse, isLoading: membersLoading } = useQuery({
    queryKey: ["/api/orgs", orgId, "members"],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/members`);
      return await res.json();
    },
    enabled: !!orgId && activeTab === "members",
    retry: (failureCount, error: any) => false,
  });
  const members = (membersResponse as any)?.data || [];

  // Pending invitations (fallback in case members endpoint doesn't include them)
  const { data: invitesResponse, isLoading: invitesLoading } = useQuery({
    queryKey: ["/api/orgs", orgId, "invitations"],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/invitations`);
      return await res.json();
    },
    enabled: !!orgId && activeTab === "members",
    retry: false,
  });
  const invitations = ((invitesResponse as any)?.data || []).map((i: any) => ({
    user_id: null,
    email: i.email,
    roleId: null,
    roleName: i.role,
    role: i.role,
    status: 'invited',
    display_name: null,
  }));
  // Merge with de-duplication: prefer actual members over invitations
  const combined = [...members, ...invitations];
  const mergedMembers = [] as any[];
  const seenKeys = new Set<string>();
  for (const m of combined) {
    const key = m.user_id ? `u:${m.user_id}` : `e:${(m.email || '').toLowerCase()}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    mergedMembers.push(m);
  }

  // Aggregate loading state to block UI until essential data arrives
  const isSettingsLoading =
    (activeTab === 'subscription' && (plansLoading || subscriptionLoading || limitsLoading)) ||
    (activeTab === 'members' && (orgMeLoading || membersLoading || invitesLoading || limitsLoading)) ||
    (activeTab === 'organization' && (orgMeLoading || membersLoading || limitsLoading)) ||
    (activeTab === 'roles' && orgMeLoading);

  // Roles & Permissions (organization-scoped)
  // Fetch available permissions from API (includes all permissions from permissions table)
  const { data: permissionsResponse } = useQuery({
    queryKey: ["/api/orgs/permissions"],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/permissions`);
      return await res.json();
    },
    staleTime: 300_000, // Cache for 5 minutes
    retry: false,
  });
  const availablePermissions = (permissionsResponse as any)?.data || [];
  const fallbackPermissions = [
    'manage_organization','manage_members','manage_projects','manage_all_projects','view_projects','view_all_projects',
    'manage_lists','ai_image_analysis',
    'confirm_overtime','export_data'
  ];
  const fallbackPermissionCategories: Record<string, string> = {
    manage_organization: 'organization',
    manage_members: 'members',
    manage_projects: 'projects',
    manage_all_projects: 'projects',
    view_projects: 'projects',
    view_all_projects: 'projects',
    confirm_overtime: 'tickets',
    manage_lists: 'lists',
    export_data: 'documents',
    ai_image_analysis: 'ai',
  };
  const permissionCategoryOrder = [
    'organization',
    'members',
    'projects',
    'tickets',
    'lists',
    'documents',
    'ai',
    'other',
  ];
  const permissionGroups = useMemo(() => {
    const rows: Array<{ name: string; category: string }> = availablePermissions.length > 0
      ? availablePermissions.map((permission: any) => ({
          name: String(permission.name),
          category: String(permission.category || 'other'),
        }))
      : fallbackPermissions.map((name) => ({
          name,
          category: fallbackPermissionCategories[name] || 'other',
        }));
    const filteredRows = rows.filter((permission) => permission.category !== 'reports' && permission.name !== 'view_reports');

    const grouped = filteredRows.reduce((acc, permission) => {
      if (!permission.name) return acc;
      const categoryKey = permission.category || 'other';
      if (!acc[categoryKey]) {
        acc[categoryKey] = [];
      }
      acc[categoryKey].push(permission.name);
      return acc;
    }, {} as Record<string, string[]>);

    Object.values(grouped).forEach((permissions) => permissions.sort());
    const orderedCategories = Object.keys(grouped).sort((a, b) => {
      const aIndex = permissionCategoryOrder.indexOf(a);
      const bIndex = permissionCategoryOrder.indexOf(b);
      const normalizedA = aIndex === -1 ? permissionCategoryOrder.length : aIndex;
      const normalizedB = bIndex === -1 ? permissionCategoryOrder.length : bIndex;
      if (normalizedA !== normalizedB) return normalizedA - normalizedB;
      return a.localeCompare(b);
    });

    return orderedCategories.map((category) => ({
      category,
      permissions: grouped[category],
    }));
  }, [availablePermissions]);

  const { data: rolesResponse, refetch: refetchRoles } = useQuery({
    queryKey: ["/api/orgs", orgId, "roles"],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles`);
      return await res.json();
    },
    enabled: !!orgId && activeTab === "roles",
    retry: false,
  });
  const roles = (rolesResponse as any)?.data || [];

  const initRolesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles/init`, { method: 'POST' });
      return await res.json();
    },
    onSuccess: () => refetchRoles(),
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ roleId, name, description, permissions }: { roleId: string; name?: string; description?: string; permissions: string[]; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify({ name, description, permissions })
      });
      return await res.json();
    },
    onSuccess: () => refetchRoles(),
    onError: (error: any) => {
      console.error('❌ Update role failed:', error);
    }
  });

  const createRoleMutation = useMutation({
    mutationFn: async ({ name, description, permissions }: { name: string; description?: string; permissions: string[]; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ name, description, permissions })
      });
      return await res.json();
    },
    onSuccess: () => {
      refetchRoles();
      setShowCreateRoleModal(false);
      setNewRoleName('');
      setNewRoleDescription('');
      setNewRolePermissions([]);
    },
    onError: (error: any) => {
      console.error('❌ Create role failed:', error);
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async ({ roleId }: { roleId: string; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/roles/${roleId}`, {
        method: 'DELETE'
      });
      return await res.json();
    },
    onSuccess: () => refetchRoles(),
    onError: (error: any) => {
      console.error('❌ Delete role failed:', error);
    }
  });

  const inviteMemberMutation = useMutation({
    mutationFn: async (vars: { orgId: string; email: string; role: string; firstName?: string; lastName?: string; mobile?: string; anrede?: string }) => {
      const { orgId, email, role, firstName, lastName, mobile, anrede } = vars;
      const res = await apiRequest(`/api/orgs/${orgId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email, role, firstName, lastName, mobile, anrede })
      });
      return await res.json();
    },
    onSuccess: async (_result, variables) => {
      rqClient.invalidateQueries({ queryKey: ["/api/orgs/me"] });
      // Also refresh members to show invited entries immediately
      if (orgId) {
        rqClient.invalidateQueries({ queryKey: ["/api/orgs", orgId, "members"] });
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId] });
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId, 'usage'] });
      }
      rqClient.invalidateQueries({ queryKey: ["/api/stripe/subscription"] });
      // Success feedback modal
      setInviteSentEmail(variables?.email || null);
      setInviteSentOpen(true);
      setInviteMemberOpen(false);
    },
    onError: (err: any) => {
      // Error feedback
      toast({ title: 'Invite failed', description: err?.message || 'Could not send invitation', variant: 'destructive' });
    }
  });

  const updateMemberMutation = useMutation({
    mutationFn: async ({ orgId, userId, roleId }: { orgId: string; userId: string; roleId: string; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/members/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ roleId })
      });
      return await res.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ["/api/orgs", orgId, "members"] });
    }
  });

  const removeMemberMutation = useMutation({
    mutationFn: async ({ orgId, userId }: { orgId: string; userId: string; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/members/${userId}`, {
        method: 'DELETE'
      });
      return await res.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ["/api/orgs", orgId, "members"] });
      rqClient.invalidateQueries({ queryKey: ["/api/orgs/me"] });
      if (orgId) {
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId] });
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId, 'usage'] });
      }
      rqClient.invalidateQueries({ queryKey: ["/api/stripe/subscription"] });
    }
  });

  const removeInvitationMutation = useMutation({
    mutationFn: async ({ orgId, email }: { orgId: string; email: string; }) => {
      const res = await apiRequest(`/api/orgs/${orgId}/invitations/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      return await res.json();
    },
    onSuccess: () => {
      rqClient.invalidateQueries({ queryKey: ["/api/orgs", orgId, "members"] });
      rqClient.invalidateQueries({ queryKey: ["/api/orgs", orgId, "invitations"] });
      rqClient.invalidateQueries({ queryKey: ["/api/orgs/me"] });
      if (orgId) {
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId] });
        rqClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId, 'usage'] });
      }
      rqClient.invalidateQueries({ queryKey: ["/api/stripe/subscription"] });
    }
  });
  
  // Price calculation functions removed - now using database values directly

  // Formatiert Preise mit Tausendertrennzeichen
  const formatPrice = (price: number) => {
    return price.toLocaleString(locale);
  };

  // Downgrade-Sperre: Prüft ob ein Plan schlechter als der aktuelle ist
  const isDowngrade = (targetPlan: string, targetUsers: number, targetBilling: string, currentPlan: string, currentUsers: number = 1, currentBilling: string = 'monthly') => {
    // Plan-Hierarchie definieren (höhere Zahl = besserer Plan)
    const planHierarchy: Record<string, number> = {
      'kostenlos': 0,
      'expert': 1,
      'pro': 2,
      'rise': 3,
      'enterprise': 4
    };

    const currentPlanLevel = planHierarchy[currentPlan] || 0;
    const targetPlanLevel = planHierarchy[targetPlan] || 0;

    // 1. Niedrigerer Plan = Downgrade
    if (targetPlanLevel < currentPlanLevel) {
      return true;
    }

    // 2. Gleicher Plan aber weniger Benutzer = Downgrade
    if (targetPlanLevel === currentPlanLevel && targetUsers < currentUsers) {
      return true;
    }

    // 3. Gleicher Plan, gleiche Benutzer, aber schlechterer Billing-Zeitraum (yearly → monthly) = Downgrade
    if (targetPlanLevel === currentPlanLevel && targetUsers === currentUsers && currentBilling === 'yearly' && targetBilling === 'monthly') {
      return true;
    }

    // Alle anderen Fälle sind Upgrades oder gleich
    return false;
  };
  
  const [billingPeriods, setBillingPeriods] = useState<Record<string, "monthly" | "yearly">>({});

  // Initialize billing periods to 'monthly' for all plans when plans are loaded
  useEffect(() => {
    if (subscriptionPlans && subscriptionPlans.length > 0) {
      setBillingPeriods(prev => {
        const updated: Record<string, "monthly" | "yearly"> = { ...prev };
        subscriptionPlans.forEach((plan: any) => {
          // Only set if not already set (preserve user selections)
          if (!updated[plan.id]) {
            updated[plan.id] = 'monthly';
          }
        });
        return updated;
      });
    }
  }, [subscriptionPlans]);

  // currentUser is derived from /api/auth/me at the top of this file

  // Fetch Stripe subscription data
  const { data: subscriptionResponse } = useQuery({
    queryKey: ["/api/stripe/subscription"],
    queryFn: async () => {
      return await apiClient.customRequest("/api/stripe/subscription");
    },
    enabled: activeTab === 'subscription' && hasPermission('manage_organization'),
    retry: (failureCount, error: any) => false,
  });

  const subscription = (subscriptionResponse as any)?.data;

  // Fetch Stripe invoices
  const { data: invoicesResponse } = useQuery({
    queryKey: ["/api/stripe/invoices"],
    queryFn: async () => {
      return await apiClient.customRequest("/api/stripe/invoices");
    },
    enabled: activeTab === 'subscription' && hasPermission('manage_organization'),
    retry: (failureCount, error: any) => false,
  });

  const invoices = (invoicesResponse as any)?.data || [];

  // Helper functions for subscription management
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: t('pages.settings:sections.subscription.statusBadges.active'), variant: 'default' as const },
      past_due: { label: t('pages.settings:sections.subscription.statusBadges.pastDue'), variant: 'destructive' as const },
      unpaid: { label: t('pages.settings:sections.subscription.statusBadges.unpaid'), variant: 'destructive' as const },
      canceled: { label: t('pages.settings:sections.subscription.statusBadges.canceled'), variant: 'secondary' as const },
      incomplete: { label: t('pages.settings:sections.subscription.statusBadges.incomplete'), variant: 'outline' as const },
      draft: { label: t('pages.settings:sections.subscription.statusBadges.draft'), variant: 'outline' as const },
      open: { label: t('pages.settings:sections.subscription.statusBadges.open'), variant: 'outline' as const },
      paid: { label: t('pages.settings:sections.subscription.statusBadges.paid'), variant: 'default' as const },
      void: { label: t('pages.settings:sections.subscription.statusBadges.void'), variant: 'secondary' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Available plans from database
  const availablePlans = subscriptionPlans || [];

  // Map subscription limits to the format expected by OrganizationTab
  const mappedSubscriptionUsage = subscriptionLimits ? {
    projectCount: subscriptionLimits.currentProjects,
    maxProjects: subscriptionLimits.maxProjects,
    teamMemberCount: subscriptionLimits.currentTeamMembers,
    maxTeamMembers: subscriptionLimits.maxTeamMembers,
    pdfCount: subscriptionLimits.currentPdfPlans,
    maxPdfPlans: subscriptionLimits.maxPdfPlans,
  } : null;
  
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phone: '',
    company: '',
    street: '',
    postalCode: '',
    city: '',
    userType: 'in-house',
    emailNotifications: false,
    pushNotifications: false,
    weeklyReports: false,
    usageHints: false,
    userExperience: false,
    dataSharing: false,
    marketing: false,
    cookies: false,
    twoFactor: false,
    audioNotifications: true,
    noSelfChanges: true,
    contractorOnly: false,
    whatsappNotifications: false,
    whatsappNumber: '',
    timezone: 'Europe/Berlin'
  });
  const { toast } = useToast();

  // Create payment intent mutation
  const createPaymentIntentMutation = useMutation({
    mutationFn: async ({ planId, billingInterval, seatCount }: { planId: string; billingInterval: 'monthly' | 'yearly'; seatCount: number }) => {
      const data = await apiClient.createPaymentIntent(planId, billingInterval, seatCount) as { success: boolean; message?: string; clientSecret?: string; paymentIntentId?: string };
      if (!data.success) {
        throw new Error(data.message || 'Failed to create payment intent');
      }
      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
      };
    },
  });

  // Confirm subscription mutation
  const confirmSubscriptionMutation = useMutation({
    mutationFn: async ({ paymentIntentId, planId, billingInterval, seatCount }: { paymentIntentId: string; planId: string; billingInterval: 'monthly' | 'yearly'; seatCount: number }) => {
      const data = await apiClient.confirmSubscription(paymentIntentId, planId, billingInterval, seatCount) as { success: boolean; message?: string };
      if (!data.success) {
        throw new Error(data.message || 'Failed to confirm subscription');
      }
      return data;
    },
    onSuccess: () => {
      // Invalidate subscription queries
      queryClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization'] });
      queryClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId, 'usage'] });
      
      // Close upgrade dialog
      setUpgradeDialog({ ...upgradeDialog, open: false });
      
      // Show success dialog
      setSuccessDialog({
        open: true,
        planName: upgradeDialog.planName,
        users: upgradeDialog.selectedUsers,
        billing: upgradeDialog.billing || 'monthly',
        price: upgradeDialog.price
      });
    },
    onError: (error: any) => {
      console.error('❌ Error confirming subscription:', error);
      toast({
        title: t('pages.settings:sections.subscription.errors.confirmationFailed'),
        description: error.message || 'Failed to confirm subscription',
        variant: 'destructive',
      });
    },
  });

  // Combined upgrade mutation for SubscriptionTab (legacy compatibility)
  // The actual upgrade flow is now handled via payment intents in SubscriptionUpgradeDialog
  const upgradeMutation = {
    isPending: createPaymentIntentMutation.isPending || confirmSubscriptionMutation.isPending,
    mutate: (_payload: any) => {
      // This is a no-op - the actual upgrade is handled in SubscriptionUpgradeDialog
      // via onCreatePaymentIntent and onPaymentSuccess callbacks
      console.log('⚠️ upgradeMutation.mutate called - this should not happen in new flow');
    },
  };

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const sessionToken = localStorage.getItem('sessionToken');
      return await apiClient.cancelSubscription();
    },
    onSuccess: (data) => {
      toast({
        title: "Erfolgreich",
        description: `Ihr Abonnement wurde gekündigt und endet automatisch am ${(data as any).cancellationDate}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/stripe/subscription"] });
      setShowCancelDialog(false);
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Kündigung fehlgeschlagen. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    },
  });

  // Formularwerte State
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    username: '',
    email: '',
    phone: '',
    mobile: '',
    company: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: '',
    profilePhotoUrl: '',
    userType: 'in-house', // Default to In-House for main users
    twoFactor: false,
    audioNotifications: true,
    noSelfChanges: true,
    contractorOnly: false,
    timezone: 'Europe/Berlin'
  });

  const passwordForm = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema),
    mode: 'onSubmit', // Only validate on submit, not on change
    reValidateMode: 'onBlur', // Re-validate on blur after first submit
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Reset form when schema changes (language change)
  useEffect(() => {
    passwordForm.reset();
  }, [passwordChangeSchema, passwordForm]);

  // Reset form when dialog opens
  useEffect(() => {
    if (showPasswordDialog) {
      passwordForm.reset();
      setPasswordChangeResult(null);
    }
  }, [showPasswordDialog, passwordForm]);

  // Handle password change function
  const handlePasswordChange = async (data: PasswordChangeFormData) => {
    try {
      // Validate passwords match before API call
      if (data.newPassword !== data.confirmPassword) {
        passwordForm.setError('confirmPassword', {
          type: 'manual',
          message: t('pages.settings:sections.password.validation.passwordsMatch')
        });
        return;
      }

      const sessionToken = localStorage.getItem('sessionToken');
      const result = await apiClient.changePassword(data.currentPassword, data.newPassword);
      setPasswordChangeResult({ success: true, message: t('pages.settings:sections.password.successMessage') });
      passwordForm.reset();
      setTimeout(() => {
        setShowPasswordDialog(false);
        setPasswordChangeResult(null);
      }, 2000);
    } catch (error: any) {
      // Handle API error messages
      const errorMessage = error?.message || t('pages.settings:sections.password.errorMessage');
      setPasswordChangeResult({ success: false, message: errorMessage });
    }
  };

  // Initialize profile form from already-fetched /api/auth/me response.
  useEffect(() => {
    if (!currentUser || isProfileInitialized) return;

    const resolvedTimezone = (currentUser as any).timezone || 'Europe/Berlin';

    const nextUserCode = (currentUser as any).userCode || '';
    setCurrentUserCode(nextUserCode);
    if (nextUserCode) {
      localStorage.setItem('userCode', nextUserCode);
    }

    const userData = {
      firstName: currentUser.firstName || '',
      lastName: currentUser.lastName || '',
      displayName: currentUser.displayName || '',
      email: currentUser.email || '',
      phone: currentUser.phone || '',
      mobile: (currentUser as any).mobile || '',
      company: (currentUser as any).company || '',
      street: (currentUser as any).street || '',
      houseNumber: (currentUser as any).houseNumber || '',
      postalCode: (currentUser as any).postalCode || '',
      city: (currentUser as any).city || '',
      country: (currentUser as any).country || '',
      profilePhotoUrl: (currentUser as any).profilePhotoUrl || '',
      userType: nextUserCode && !nextUserCode.includes('#') ? 'in-house' : 'contractor',
      emailNotifications: false,
      pushNotifications: false,
      weeklyReports: false,
      usageHints: false,
      userExperience: false,
      dataSharing: false,
      marketing: false,
      cookies: false,
      twoFactor: false,
      audioNotifications: true,
      noSelfChanges: true,
      contractorOnly: false,
      timezone: resolvedTimezone
    };

    setFormValues((prev) => ({
      ...prev,
      ...userData,
      username: (currentUser as any).username || '',
    }));
    setOriginalValues({
      ...(userData as any),
      username: (currentUser as any).username || '',
      timezone: resolvedTimezone,
    });
    setIsProfileInitialized(true);
  }, [currentUser, isProfileInitialized]);

  // Remove old hardcoded subscription data fetching - now using real subscription hooks

  // Handle save settings function
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      
      // Save profile data (position is not sent - it's org-context-aware and computed by API)
      const normalizedDisplayName = [formValues.firstName, formValues.lastName]
        .map((value: string) => value?.trim())
        .filter(Boolean)
        .join(' ');
      const profileData = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        displayName: normalizedDisplayName || formValues.displayName,
        email: formValues.email,
        phone: formValues.phone,
        mobile: formValues.mobile,
        company: formValues.company,
        street: formValues.street,
        houseNumber: formValues.houseNumber,
        postalCode: formValues.postalCode,
        city: formValues.city,
        country: formValues.country,
        profilePhotoUrl: formValues.profilePhotoUrl || null
      };

      // Save settings data
      const settingsData = {
        twoFactor: formValues.twoFactor,
        audioNotifications: formValues.audioNotifications,
        noSelfChanges: formValues.noSelfChanges,
        contractorOnly: formValues.contractorOnly,
        timezone: formValues.timezone
      };

      const results = await Promise.all([
        apiClient.updateProfile(profileData),
        apiClient.updateSettings(settingsData)
      ]);
      
      if (results && results.length > 0) {
        // Reload user data from server to get latest values (force fresh data)
        const data = await apiClient.getMe();
        const user = data.user;
          
        // Update form values with fresh data from server (position is not in formValues - it comes from currentUser)
        const freshUserData = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          displayName: user.displayName || '',
          email: user.email || '',
          phone: user.phone || '',
          mobile: (user as any).mobile || '',
          company: (user as any).company || '',
          street: (user as any).street || '',
          houseNumber: (user as any).houseNumber || '',
          postalCode: (user as any).postalCode || '',
          city: (user as any).city || '',
          country: (user as any).country || '',
          profilePhotoUrl: (user as any).profilePhotoUrl || '',
          userType: currentUserCode && !currentUserCode.includes('#') ? 'in-house' : 'contractor',
          twoFactor: formValues.twoFactor,
          audioNotifications: formValues.audioNotifications,
          noSelfChanges: formValues.noSelfChanges,
          contractorOnly: formValues.contractorOnly,
          timezone: formValues.timezone
        };
        
        const completeUserData = {
          ...freshUserData,
          username: user.username || '',
          emailNotifications: false,
          pushNotifications: false,
          weeklyReports: false,
          usageHints: false,
          userExperience: false,
          dataSharing: false,
          marketing: false,
          cookies: false,
          whatsappNotifications: false,
          whatsappNumber: '',
          twoFactor: formValues.twoFactor,
          audioNotifications: formValues.audioNotifications,
          noSelfChanges: formValues.noSelfChanges,
          contractorOnly: formValues.contractorOnly,
          timezone: formValues.timezone
        };
        setFormValues(completeUserData);
        setOriginalValues(completeUserData);
        
        // Invalidate React Query cache to refresh user data
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        if (orgId) {
          queryClient.invalidateQueries({ queryKey: ['/api/orgs', orgId, 'members'] });
          queryClient.invalidateQueries({ queryKey: ['/api/orgs', orgId, 'invitations'] });
        }
        
        // Reset change detection after successful save
        setHasChanges(false);
        toast({
          title: t('pages.settings:toasts.saveSuccessTitle'),
          description: t('pages.settings:toasts.saveSuccessDesc'),
        });
      } else {
        throw new Error('Fehler beim Speichern');
      }
    } catch (error) {
      toast({
        title: "Fehler beim Speichern",
        description: "Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel - reset to original values
  const handleCancel = () => {
    setFormValues({
      ...originalValues,
      profilePhotoUrl: (originalValues as { profilePhotoUrl?: string }).profilePhotoUrl ?? '',
      username: (originalValues as any).username || '',
      mobile: (originalValues as any).mobile || '',
      houseNumber: (originalValues as any).houseNumber || '',
      country: (originalValues as any).country || ''
    });
    setHasChanges(false);
  };

  // Check for changes whenever form values change
  useEffect(() => {
    const hasChanged = JSON.stringify(formValues) !== JSON.stringify(originalValues);
    setHasChanges(hasChanged);
  }, [formValues, originalValues]);

  useEffect(() => {
    if (isRestrictedRole && activeTab !== 'account') {
      setActiveTab('account');
    }
  }, [isRestrictedRole, activeTab]);

  const settingsTabs = isRestrictedRole
    ? [
        {
          id: 'account',
          label: t('pages.settings:tabs.account'),
          active: activeTab === 'account',
          onClick: () => handleTabChange('account'),
        },
      ]
    : [
        {
          id: "account",
          label: t('pages.settings:tabs.account'),
          active: activeTab === "account",
          onClick: () => handleTabChange("account"),
          onMouseEnter: () => prefetchSettingsTab('account'),
        },
        {
          id: "organization",
          label: t('pages.settings:tabs.organization'),
          active: activeTab === "organization",
          onClick: () => handleTabChange("organization"),
          onMouseEnter: () => prefetchSettingsTab('organization'),
        },
        {
          id: "projects",
          label: t('pages.settings:tabs.projects'),
          active: activeTab === "projects",
          onClick: () => handleTabChange("projects"),
          onMouseEnter: () => prefetchSettingsTab('projects'),
        },
        {
          id: "members",
          label: t('pages.settings:tabs.members'),
          active: activeTab === "members",
          onClick: () => handleTabChange("members"),
          onMouseEnter: () => prefetchSettingsTab('members'),
        },
        {
          id: "roles",
          label: t('pages.settings:tabs.roles'),
          active: activeTab === "roles",
          onClick: () => handleTabChange("roles"),
          onMouseEnter: () => prefetchSettingsTab('roles'),
        },
        {
          id: "subscription",
          label: t('pages.settings:tabs.subscription'),
          active: activeTab === "subscription",
          onClick: () => handleTabChange("subscription"),
          onMouseEnter: () => prefetchSettingsTab('subscription'),
        },
        {
          id: "support",
          label: t('pages.settings:tabs.support'),
          active: activeTab === "support",
          onClick: () => handleTabChange("support"),
          onMouseEnter: () => prefetchSettingsTab('support'),
        },
        {
          id: "help",
          label: t('pages.settings:tabs.faq'),
          active: activeTab === "help",
          onClick: () => handleTabChange("help"),
          onMouseEnter: () => prefetchSettingsTab('help'),
        },
      ];

  return (
    <AppShell>
      <PageHeaderLayout
          pageTitle={t('pages.settings:title')}
          tabs={
            <HeaderTabNav items={settingsTabs} />
          }
          actionButtons={
            activeTab === 'account' || activeTab === 'projects' ? (
              <HeaderActionGroup>
                <Button
                  variant="pillOutline"
                  onClick={handleCancel}
                  size="sm"
                  className="bg-white hover:bg-white"
                  disabled={!hasChanges}
                >
                  {t('pages.settings:header.cancel')}
                </Button>
                <Button 
                  onClick={handleSaveSettings}
                  variant="pillOutline"
                  size="sm"
                  className="bg-white hover:bg-white"
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? t('pages.settings:header.saving') : t('pages.settings:header.save')}
                </Button>
              </HeaderActionGroup>
            ) : activeTab === 'members' ? (
              <HeaderActionGroup>
                <PermissionButton
                  variant="pillOutline"
                  size="sm"
                  className="bg-white hover:bg-white"
                  permission={['manage_members', 'manage_organization']}
                  onClick={() => setInviteMemberOpen(true)}
                >
                  {t('pages.settings:sections.invite.title')}
                </PermissionButton>
              </HeaderActionGroup>
            ) : activeTab === 'roles' ? (
              <HeaderActionGroup>
                <PermissionButton
                  variant="pillOutline"
                  size="sm"
                  className="bg-white hover:bg-white"
                  permission="manage_organization"
                  disabled={selectedRoleIds.length === 0}
                  onClick={async () => {
                    const confirmed = await confirm({
                      message: t('pages.settings:sections.roles.deleteConfirm', { count: selectedRoleIds.length }),
                      variant: 'destructive',
                      confirmText: t('common:alerts.delete'),
                    });
                    if (!confirmed) return;
                    selectedRoleIds.forEach((roleId) => {
                      deleteRoleMutation.mutate({ roleId });
                    });
                    setSelectedRoleIds([]);
                  }}
                >
                  {t('pages.settings:sections.roles.deleteSelected')}
                </PermissionButton>
                <PermissionButton
                  variant="pillOutline"
                  size="sm"
                  className="bg-white hover:bg-white"
                  permission="manage_organization"
                  onClick={() => setShowCreateRoleModal(true)}
                >
                  {t('pages.settings:sections.roles.create')}
                </PermissionButton>
              </HeaderActionGroup>
            ) : undefined
          }
        >
          <div className={`${activeTab === 'support' ? 'h-full flex flex-col' : ''} p-4 pt-2 -mt-[8px] min-w-[1328px] ${isSettingsLoading ? 'pointer-events-none opacity-50' : ''}`}>
            <div className="flex items-start gap-4">
            <div className={`${activeTab === 'support' ? 'flex-1 min-h-0 flex flex-col' : ''} min-w-[1112px] max-w-[1112px]`}>
              <div className={`bg-[#DDDDDD] rounded-[24px] p-[32px] settings-tab-container ${activeTab === 'support' ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
              {/* Account Tab Content */}
            {activeTab === "account" && (
              <AccountTab
                currentUserCode={currentUserCode}
                currentUser={currentUser}
                formValues={formValues}
                setFormValues={setFormValues as any}
                setHasChanges={setHasChanges}
                setShowPasswordDialog={setShowPasswordDialog}
                hasPermission={hasPermission}
                preferences={preferences}
                preferencesLoading={preferencesLoading}
                updatePreferences={updatePreferences as any}
                activeOrgRole={activeOrgRole}
                activeOrganizationId={activeOrganizationId}
              />
            )}

            
            {activeTab === "organization" && (
              <RequirePermission permission="manage_organization">
                <OrganizationTab />
              </RequirePermission>
            )}

            {activeTab === "members" && (
              <RequirePermission permission={['manage_members', 'manage_organization']}>
                <MembersTab
                  orgInfo={orgInfo}
                  orgId={orgId}
                  currentUserRoleLabel={`${t('pages.settings:sections.members.table.role')}: ${resolvedRoleLabel}`}
                  subscriptionUsage={mappedSubscriptionUsage}
                  subscriptionInfo={organizationSubscription ? {
                    pricePerSeat: organizationSubscription.billing_interval === 'yearly'
                      ? (organizationSubscription.price_per_seat_yearly || 0) / 100
                      : (organizationSubscription.price_per_seat_monthly || 0) / 100,
                    billingInterval: organizationSubscription.billing_interval,
                    currency: organizationSubscription.currency,
                    planName: organizationSubscription.plan_name,
                  } : undefined}
                  roles={roles}
                  inviteRole={inviteRole}
                  setInviteRole={setInviteRole}
                  members={mergedMembers}
                  membersLoading={membersLoading || invitesLoading}
                  hasPermission={hasPermission}
                  inviteMember={(args) => inviteMemberMutation.mutate(args)}
                  inviteMemberLoading={inviteMemberMutation.isPending}
                  updateMemberRole={({ orgId, userId, roleId }) => updateMemberMutation.mutate({ orgId, userId, roleId })}
                  removeMember={({ orgId, userId }) => removeMemberMutation.mutate({ orgId, userId })}
                  removeMemberLoading={removeMemberMutation.isPending}
                  removeInvitation={({ orgId, email }) => removeInvitationMutation.mutate({ orgId, email })}
                  removeInvitationLoading={removeInvitationMutation.isPending}
                  inviteSentOpen={inviteSentOpen}
                  inviteSentEmail={inviteSentEmail}
                  onCloseInviteSent={() => setInviteSentOpen(false)}
                  inviteMemberOpen={inviteMemberOpen}
                  setInviteMemberOpen={setInviteMemberOpen}
                />
              </RequirePermission>
            )}

            {/* Roles & Permissions Tab */}
            {activeTab === "roles" && (
              <RequirePermission permission="manage_organization">
                <RolesTab
                  orgId={orgId}
                  roles={roles}
                  canManageRoles={canManageRoles}
                  onOpenEdit={(r) => { setEditingRole(r as any); setShowEditRoleModal(true); }}
                  selectedRoleIds={selectedRoleIds}
                  setSelectedRoleIds={setSelectedRoleIds}
                />
              </RequirePermission>
            )}
            

            {activeTab === "projects" && (
              <RequirePermission permission={['manage_projects', 'manage_all_projects', 'manage_organization']}>
                <ProjectsTab
                  preferences={preferences as any}
                  preferencesLoading={preferencesLoading as any}
                  updatePreferences={updatePreferences as any}
                />
              </RequirePermission>
            )}

            

            {/* Subscription Tab Content */}
            {activeTab === "subscription" && (
              <RequirePermission permission="manage_organization">
                <SubscriptionTab
                  subscriptionPlan={organizationSubscription}
                  subscriptionUsage={subscriptionLimits}
                  subscription={organizationSubscription}
                  invoices={invoices}
                  availablePlans={availablePlans}
                  selectedProUsers={selectedProUsers}
                  setSelectedProUsers={setSelectedProUsers}
                  selectedRiseUsers={selectedRiseUsers}
                  setSelectedRiseUsers={setSelectedRiseUsers}
                  billingPeriods={billingPeriods}
                  setBillingPeriods={setBillingPeriods}
                  calculateProPrice={() => 0} // Deprecated - using database values
                  calculateRisePrice={() => 0} // Deprecated - using database values
                  formatPrice={formatPrice}
                  isDowngrade={isDowngrade}
                  formatDate={formatDate}
                  getStatusBadge={getStatusBadge}
                  upgradeMutation={upgradeMutation as any}
                  setUpgradeDialog={setUpgradeDialog}
                  hasPermission={hasPermission}
                  showCancelDialog={showCancelDialog}
                  setShowCancelDialog={setShowCancelDialog}
                  cancelMutation={cancelMutation as any}
                  organizationId={activeOrganizationId}
                />
              </RequirePermission>
            )}

            {/* Support Tab Content - uses flex-1 to fill available space */}
            {activeTab === "support" && (
              <div className="flex-1 min-h-0 flex flex-col">
                <SupportTab />
              </div>
            )}

            {/* Projekt Tab Content - FULLY HIDDEN FOR "Admin" super admin (MP-010-250000) */}
            {activeTab === "projekt" && currentUserCode !== "MP-010-250000" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('pages.settings:sections.projects.title')}</h2>
                  <p className="text-slate-600 mb-6">
                    {t('pages.settings:sections.projects.intro')}
                  </p>
                  
                  <Card className="border-slate-200">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-medium text-slate-900 mb-4">{t('pages.settings:sections.projects.standard')}</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-sm font-medium text-slate-900">Automatische Backups</h4>
                            <p className="text-xs text-slate-600">Tägliche Sicherung Ihrer Projektdaten</p>
                          </div>
                          <Switch defaultChecked />
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-sm font-medium text-slate-900">{t('pages.settings:sections.projects.emailNotificationsTitle')}</h4>
                            <p className="text-xs text-slate-600">{t('pages.settings:sections.projects.emailNotificationsDesc')}</p>
                          </div>
                          <Switch defaultChecked />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

              </div>
            </div>
            <SettingsInfoChips
              userLoading={userLoading}
              resolvedUserCode={resolvedUserCode}
              userError={userError}
              resolvedRoleLabel={resolvedRoleLabel}
            />
            </div>
          </div>
        </PageHeaderLayout>

      {isSettingsLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-50">
          <LogoLoader size="sm" text={t('pages.settings:loading')} showText />
        </div>
      )}
      
      {/* Upgrade Dialog */}
      <SubscriptionUpgradeDialog
        open={upgradeDialog.open}
        onOpenChange={(open) => {
          console.log(`🔄 SETTINGS Dialog onOpenChange: ${open}`);
          setUpgradeDialog({ ...upgradeDialog, open });
        }}
        newPlan={upgradeDialog.planId}
        newPlanName={upgradeDialog.planName}
        price={upgradeDialog.price}
        duration={upgradeDialog.duration}
        selectedUsers={upgradeDialog.selectedUsers}
        currentUsers={upgradeDialog.currentUsers || subscriptionLimits?.currentTeamMembers || 1}
        maxUsers={upgradeDialog.maxUsers}
        maxProjects={upgradeDialog.maxProjects}
        features={upgradeDialog.features}
        currentPlan={subscriptionPlanNameToSlug(
          organizationSubscription?.plan_name ?? (subscription as { plan_name?: string } | undefined)?.plan_name
        )}
        currentPlanBilling={
          organizationSubscription?.billing_interval === 'yearly' ? 'yearly' : 'monthly'
        }
        billing={upgradeDialog.billing}
        isPerSeat={upgradeDialog.isPerSeat}
        pricePerSeat={upgradeDialog.pricePerSeat}
        minSeats={upgradeDialog.minSeats}
        maxSeats={upgradeDialog.maxSeats}
        onCreatePaymentIntent={async (seatCount) => {
          try {
            const result = await createPaymentIntentMutation.mutateAsync({
              planId: upgradeDialog.planId,
              billingInterval: upgradeDialog.billing || 'monthly',
              seatCount: seatCount || upgradeDialog.selectedUsers || upgradeDialog.minSeats || 1,
            });
            return result;
          } catch (error: any) {
            console.error('❌ Error creating payment intent:', error);
            toast({
              title: t('pages.settings:sections.subscription.errors.paymentIntentFailed'),
              description: error.message || 'Failed to create payment intent',
              variant: 'destructive',
            });
            return null;
          }
        }}
        onPaymentSuccess={async () => {
          if (!upgradeDialog.planId) return;
          
          // Get payment intent ID from the createPaymentIntent mutation result
          const paymentIntentId = createPaymentIntentMutation.data?.paymentIntentId;
          
          if (!paymentIntentId) {
            console.error('❌ Payment intent ID not found');
            toast({
              title: t('pages.settings:sections.subscription.errors.paymentIntentIdMissing'),
              description: 'Payment intent ID not found. Please try again.',
              variant: 'destructive',
            });
            return;
          }
          
          try {
            await confirmSubscriptionMutation.mutateAsync({
              paymentIntentId,
              planId: upgradeDialog.planId,
              billingInterval: upgradeDialog.billing || 'monthly',
              seatCount: createPaymentIntentMutation.variables?.seatCount || upgradeDialog.selectedUsers || upgradeDialog.minSeats || 1,
            });
          } catch (error: any) {
            console.error('❌ Error confirming subscription:', error);
            // Error handling is done in mutation onError
          }
        }}
        onConfirm={() => {
          // Legacy handler - not used with Stripe flow
          console.log('🎯 Legacy onConfirm called');
        }}
        isLoading={createPaymentIntentMutation.isPending || confirmSubscriptionMutation.isPending}
      />
      
      {/* Success Dialog */}
      <Dialog open={successDialog.open} onOpenChange={(open) => setSuccessDialog({ ...successDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-2 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <DialogTitle className="text-sm font-semibold text-[#333333]">
                {t('pages.settings:sections.subscription.successDialog.title')}
              </DialogTitle>
              </div>
            <DialogDescription className="text-xs text-[#666666]">
              {t('pages.settings:sections.subscription.successDialog.subtitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="card-container p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-slate-700">
                    {t('pages.settings:sections.subscription.successDialog.newPlan')}:
                  </span>
                  <span className="text-xs font-medium text-slate-900">{successDialog.planName}</span>
                </div>
                {successDialog.users > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-700">
                      {t('pages.settings:sections.subscription.successDialog.users')}:
                    </span>
                    <span className="text-xs font-medium text-slate-900">{successDialog.users}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-slate-700">
                    {t('pages.settings:sections.subscription.successDialog.billing')}:
                  </span>
                  <span className="text-xs font-medium text-slate-900">
                    {successDialog.billing === 'yearly' 
                      ? t('pages.settings:sections.subscription.billing.yearly')
                      : t('pages.settings:sections.subscription.billing.monthly')}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-sm font-semibold text-slate-900">{successDialog.price}</span>
                  <span className="text-xs text-slate-600">
                    {successDialog.billing === 'yearly' 
                      ? t('pages.settings:sections.subscription.successDialog.perYear')
                      : t('pages.settings:sections.subscription.successDialog.perMonth')}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700 text-center">
                <strong>{t('pages.settings:sections.subscription.successDialog.note')}:</strong>{' '}
                {t('pages.settings:sections.subscription.successDialog.noteMessage')}
              </p>
            </div>
            
            <Button
              onClick={() => setSuccessDialog({ ...successDialog, open: false })}
              className="w-full"
            >
              {t('pages.settings:sections.subscription.successDialog.understood')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Support Dialog */}
      <SupportDialog 
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
      />


      {/* Create Position Modal */}
      <Dialog open={showCreateRoleModal} onOpenChange={setShowCreateRoleModal}>
        <DialogContent className="max-w-2xl bg-[#DDDDDD] rounded-[24px] [&>button]:hidden">
          <DialogHeader className="flex flex-row items-start justify-between">
            <div>
              <DialogTitle>{t('pages.settings:sections.roles.create')}</DialogTitle>
              <DialogDescription>
                {t('pages.settings:sections.roles.createDescription')}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="pillOutline"
                size="sm"
                className="bg-white hover:bg-white"
                onClick={() => setShowCreateRoleModal(false)}
              >
                {t('common:alerts.cancel')}
              </Button>
              <Button
                variant="pillOutline"
                size="sm"
                className="bg-white hover:bg-white"
                onClick={() => createRoleMutation.mutate({ 
                  name: newRoleName, 
                  description: newRoleDescription, 
                  permissions: newRolePermissions 
                }, {
                  onSuccess: () => {
                    setShowCreateRoleModal(false);
                    setNewRoleName('');
                    setNewRoleDescription('');
                    setNewRolePermissions([]);
                  }
                })}
                disabled={!newRoleName || createRoleMutation.isPending}
              >
                {createRoleMutation.isPending ? t('pages.settings:sections.roles.creating') : t('pages.settings:sections.roles.create')}
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <CornerLabelField label={t('pages.settings:sections.roles.fields.name')}>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder={t('pages.settings:sections.roles.fields.namePlaceholder')}
              />
            </CornerLabelField>
            <CornerLabelField label={t('pages.settings:sections.roles.fields.description')}>
              <Input
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
                placeholder={t('pages.settings:sections.roles.fields.descriptionPlaceholder')}
              />
            </CornerLabelField>
            <div>
              <div className="text-xs font-medium text-[#000000] mb-2">{t('pages.settings:sections.roles.fields.permissions')}</div>
              <div className="space-y-3">
                {permissionGroups.map(({ category, permissions }) => (
                  <div key={category}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-700 mb-2">
                      {t(`pages.settings:sections.roles.permissionCategories.${category}`, { defaultValue: category })}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {permissions.map((permission) => (
                        <label key={permission} className="flex items-center space-x-2 text-sm hover:bg-[#E8E8E8] p-1 rounded">
                          <input
                            type="checkbox"
                            checked={newRolePermissions.includes(permission)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewRolePermissions([...newRolePermissions, permission]);
                              } else {
                                setNewRolePermissions(newRolePermissions.filter(p => p !== permission));
                              }
                            }}
                            className="corner-checkbox"
                          />
                          <span className="text-xs">{t(`sections.permissions.${permission}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Position Modal */}
      <Dialog open={showEditRoleModal} onOpenChange={setShowEditRoleModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Position: {editingRole?.name}</DialogTitle>
            <DialogDescription>
              Modify the position name, description, and permissions.
            </DialogDescription>
          </DialogHeader>
          {editingRole && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Position Name</label>
                <Input
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({...editingRole, name: e.target.value})}
                  disabled={editingRole.is_system && editingRole.name === 'owner'}
                  className="mt-1"
                />
                {editingRole.is_system && editingRole.name === 'owner' && (
                  <p className="text-xs text-slate-500 mt-1">System owner position name cannot be changed</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={editingRole.description || ''}
                  onChange={(e) => setEditingRole({...editingRole, description: e.target.value})}
                  placeholder="Brief description of this position"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Permissions</label>
                <div className="space-y-3 mt-2 border rounded p-3">
                  {permissionGroups.map(({ category, permissions }) => {
                    const currentPermissions = new Set((editingRole.permissions || []).filter((x: any) => x.allowed).map((x: any) => x.permission));
                    return (
                      <div key={category}>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-700 mb-2">
                          {t(`pages.settings:sections.roles.permissionCategories.${category}`, { defaultValue: category })}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {permissions.map((permission) => (
                            <label key={permission} className="flex items-center space-x-2 text-sm hover:bg-slate-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={currentPermissions.has(permission)}
                                onChange={(e) => {
                                  const newPermissions = Array.from(currentPermissions);
                                  if (e.target.checked) {
                                    newPermissions.push(permission);
                                  } else {
                                    const index = newPermissions.indexOf(permission);
                                    if (index > -1) newPermissions.splice(index, 1);
                                  }
                                  setEditingRole({
                                    ...editingRole,
                                    permissions: newPermissions.map(p => ({ permission: p, allowed: true }))
                                  });
                                }}
                                className="rounded"
                              />
                              <span className="text-xs">{t(`sections.permissions.${permission}`)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {t('pages.settings:sections.roles.fields.permissionsHelp')}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRoleModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const permissions = (editingRole?.permissions || [])
                  .filter((p: any) => p.allowed)
                  .map((p: any) => p.permission);
                updateRoleMutation.mutate({ 
                  roleId: editingRole.id,
                  name: editingRole.name, 
                  description: editingRole.description, 
                  permissions 
                }, {
                  onSuccess: () => {
                    setShowEditRoleModal(false);
                  }
                });
              }}
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages.settings:sections.password.title')}</DialogTitle>
            <DialogDescription>
              {t('pages.settings:sections.password.description')}
            </DialogDescription>
          </DialogHeader>
          
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.settings:sections.password.currentPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t('pages.settings:sections.password.currentPasswordPlaceholder') as string}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.settings:sections.password.newPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t('pages.settings:sections.password.newPasswordPlaceholder') as string}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.settings:sections.password.confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t('pages.settings:sections.password.confirmPasswordPlaceholder') as string}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {passwordChangeResult && (
                <div className={`p-3 rounded-md text-sm ${
                  passwordChangeResult.success 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {passwordChangeResult.message}
                </div>
              )}
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordDialog(false);
                    passwordForm.reset();
                    setPasswordChangeResult(null);
                  }}
                >
                  {t('pages.settings:sections.password.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={passwordForm.formState.isSubmitting}
                >
                  {passwordForm.formState.isSubmitting ? t('pages.settings:sections.password.changing') : t('pages.settings:sections.password.changeButton')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
